"use client";

import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { useTranslations } from "next-intl";
import { track } from "@/components/analytics/track";

const VIDEO_SRC = "/videos/product-demo.mp4";
const POSTER_SRC = "/videos/product-demo-poster.jpg";
/** 16:9 ScreenKite export (1920 × 1080). */
const ASPECT = "16 / 9";

const BEATS = [
  "demoBeat1",
  "demoBeat2",
  "demoBeat3",
  "demoBeat4",
  "demoBeat5",
] as const;

/**
 * Product film — poster + play overlay, then native controls.
 */
export function ProductDemo() {
  const t = useTranslations("Home");
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasVideo, setHasVideo] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(VIDEO_SRC, { method: "HEAD" })
      .then((res) => {
        if (!cancelled) setHasVideo(res.ok);
      })
      .catch(() => {
        if (!cancelled) setHasVideo(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function playFilm() {
    setPlaying(true);
    track("cta_click", { meta: { label: "demo_play" } });
    requestAnimationFrame(() => {
      void videoRef.current?.play();
    });
  }

  return (
    <section
      id="product-demo"
      className="scroll-mt-24 bg-gradient-to-b from-slate-950 via-slate-900/80 to-slate-950 px-4 py-24 sm:px-6"
    >
      <div className="mx-auto w-full max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="mb-10 text-center"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-teal-light">
            {t("demoEyebrow")}
            <span className="ml-3 font-mono tracking-normal text-slate-500">
              {t("demoDuration")}
            </span>
          </p>
          <h2 className="mx-auto mb-4 max-w-[20.5rem] text-[clamp(1.5rem,6vw,3rem)] font-bold tracking-tight text-pretty text-slate-100 sm:max-w-none">
            {t("demoTitle")}
          </h2>
          <p className="mx-auto w-full max-w-[20.5rem] text-base leading-relaxed text-slate-400 sm:max-w-2xl sm:text-lg">
            {t("demoIntro")}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, delay: 0.08 }}
          className="relative"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-4 rounded-[1.75rem] bg-teal-primary/20 blur-3xl sm:-inset-8"
          />
          <div
            className="relative overflow-hidden rounded-2xl border border-teal-primary/40 bg-slate-950 shadow-[0_24px_80px_-24px_rgba(45,138,110,0.5)]"
            style={{ aspectRatio: ASPECT }}
          >
            <span
              aria-hidden
              className="pointer-events-none absolute left-3 top-3 z-10 h-5 w-5 border-l-2 border-t-2 border-teal-light/70 sm:left-4 sm:top-4 sm:h-7 sm:w-7"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute right-3 top-3 z-10 h-5 w-5 border-r-2 border-t-2 border-teal-light/70 sm:right-4 sm:top-4 sm:h-7 sm:w-7"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute bottom-3 left-3 z-10 h-5 w-5 border-b-2 border-l-2 border-teal-light/70 sm:bottom-4 sm:left-4 sm:h-7 sm:w-7"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute bottom-3 right-3 z-10 h-5 w-5 border-b-2 border-r-2 border-teal-light/70 sm:bottom-4 sm:right-4 sm:h-7 sm:w-7"
            />
            {hasVideo ? (
              <>
                <video
                  ref={videoRef}
                  className="h-full w-full bg-slate-950 object-contain"
                  controls={playing}
                  playsInline
                  preload="metadata"
                  poster={POSTER_SRC}
                  aria-label={t("demoAria")}
                >
                  <source src={VIDEO_SRC} type="video/mp4" />
                  {t("demoNoSupport")}
                </video>
                {!playing ? (
                  <button
                    type="button"
                    onClick={playFilm}
                    className="group absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-4 bg-slate-950/40 transition-colors hover:bg-slate-950/25"
                    aria-label={t("demoPlay")}
                  >
                    <span className="relative h-20 w-20">
                      <span className="play-ring" />
                      <span className="play-ring play-ring-delay" />
                      <span className="relative z-10 flex h-20 w-20 items-center justify-center rounded-full bg-maroon text-white shadow-lg shadow-maroon/50 transition-transform group-hover:scale-105">
                        <Play className="ml-1 h-8 w-8 fill-current" />
                      </span>
                    </span>
                    <span className="rounded-full bg-slate-950/80 px-4 py-1.5 text-sm font-semibold tracking-wide text-slate-100 backdrop-blur-sm">
                      {t("demoPlay")}
                      <span className="ml-2 font-mono text-slate-400">
                        {t("demoDuration")}
                      </span>
                    </span>
                  </button>
                ) : null}
              </>
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-slate-950/80 px-6 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full border border-teal-primary/50 bg-teal-primary/20">
                  <Play className="ml-0.5 h-6 w-6 text-teal-light" />
                </div>
                <p className="font-medium text-slate-300">{t("demoComingSoon")}</p>
                <p className="max-w-md text-sm text-slate-500">
                  Add{" "}
                  <code className="text-teal-light/90">
                    public/videos/product-demo.mp4
                  </code>{" "}
                  (H.264) to enable playback.
                </p>
              </div>
            )}
          </div>
        </motion.div>

        <ol className="mt-8 flex w-full flex-wrap justify-center gap-2 sm:gap-3">
          {BEATS.map((key, i) => (
            <li
              key={key}
              className="inline-flex items-center gap-2 rounded-full border border-slate-700/70 bg-slate-900/70 px-3 py-1.5 text-xs text-slate-300 sm:text-sm"
            >
              <span className="font-mono text-teal-light/80">
                {String(i + 1).padStart(2, "0")}
              </span>
              {t(key)}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
